import { overtimeConstants } from './OvertimeConstants';

// ============================================
// Utility Functions for Normalization
// ============================================

/**
 * Creates a composite ID for shift signups
 */
const createSignupId = (employeeId, shiftName) => `${employeeId}-${shiftName}`;

/**
 * Normalizes an array of employees from API response
 * into normalized state structure
 */
export const normalizeEmployees = (employeesArray) => {
    const employees = {
        byId: {},
        allIds: []
    };
    
    const shiftSignups = {
        byId: {},
        byEmployeeId: {}
    };

    employeesArray.forEach((employee, index) => {
        const { otShiftSignUps, ...employeeData } = employee;
        
        // Store employee without nested signups
        // First employee expanded, rest collapsed
        employees.byId[employee.aaId] = {
            ...employeeData,
            collapsed: index !== 0
        };
        employees.allIds.push(employee.aaId);
        
        // Normalize shift signups
        shiftSignups.byEmployeeId[employee.aaId] = [];
        
        if (otShiftSignUps) {
            const shiftDate = employeeData.otDayShifts?.[0]?.shiftDate || '';
            otShiftSignUps.forEach((signup) => {
                const signupId = createSignupId(employee.aaId, signup.shiftName);
                shiftSignups.byId[signupId] = {
                    ...signup,
                    id: signupId,
                    employeeId: employee.aaId,
                    schedBidGroup: employee.schedBidGroup || '',
                    otGroup: employee.otGroup || '',
                    shiftDate,
                };
                shiftSignups.byEmployeeId[employee.aaId].push(signupId);
            });
        }
    });

    return { employees, shiftSignups };
};

// ============================================
// Initial State (Normalized)
// ============================================
const initialState = {
    employees: { byId: {}, allIds: [] },
    shiftSignups: { byId: {}, byEmployeeId: {} },
    overtimeSlots: [],
    showLoader: false,
    fetchSuccess: null,
    fetchError: null,
    otSkillsSuccess: null,
    otSkillsData: null,
    callSheetData: null,
    requirementFulfilled: false,
    votReportData: [],
    votReportLoading: false,
    votReportError: null,
    pendingCount: null,
    wbPolling: false,
    callLogEntries: {},
};

/**
 * Derives pendingCount from normalized signups when the API does not provide it.
 * Counts signups with status ACCEPT whose wbStatus is not true (i.e. still pending WB confirmation).
 * TODO: Remove mock once API provides pendingCount
 */
const derivePendingCount = (shiftSignups) => {
    let count = 0;
    Object.values(shiftSignups.byId).forEach((signup) => {
        const status = (signup.status || '').toUpperCase();
        if (status === 'ACCEPT' && signup.wbStatus !== true) {
            count++;
        }
    });
    return count;
};

// ============================================
// Reducer
// ============================================
export const OvertimeReducer = (state = initialState, action) => {
    switch (action.type) {
        case overtimeConstants.SHOW_OVERTIME_LOADER: {
            return {
                ...state,
                showLoader: action.data
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_SUCCESS: {
            // Normalize incoming data from API
            const callSheetList = Array.isArray(action.data?.callSheetList) 
                ? action.data.callSheetList 
                : [];
            const { employees, shiftSignups } = normalizeEmployees(callSheetList);
            const shiftsReqs = action.requestParams?.shiftsReqs || [];
            const currentShiftName = action.data?.callSheetWorkingShift?.current || '';
            const usedIndices = new Set();
            const matchIdx = shiftsReqs.findIndex(
                (r) => (r.shiftType || '').toUpperCase() === currentShiftName.toUpperCase()
            );
            const matchingReq = matchIdx >= 0 ? shiftsReqs[matchIdx] : {};
            if (matchIdx >= 0) usedIndices.add(matchIdx);
            const upcomingRaw = action.data?.callSheetWorkingShift?.upcoming || [];
            const enrichedUpcoming = upcomingRaw.map((entry) => {
                const shiftName = typeof entry === 'string'
                    ? entry
                    : (entry?.shiftName || entry?.shiftType || entry?.name || '');
                const apiStartTime = entry !== null && typeof entry === 'object' ? (entry.startTime || '') : '';
                const apiEndTime = entry !== null && typeof entry === 'object' ? (entry.endTime || '') : '';
                const idx = shiftsReqs.findIndex(
                    (r, i) => !usedIndices.has(i) && (r.shiftType || '').toUpperCase() === shiftName.toUpperCase()
                );
                if (idx >= 0) usedIndices.add(idx);
                const req = idx >= 0 ? shiftsReqs[idx] : {};
                return { name: shiftName, startTime: apiStartTime || req.startTime || '', endTime: apiEndTime || req.endTime || '' };
            });
            const callSheetData = {
                callSheetId: action.data?.votId || '',
                sessionToken: action.data?.session?.sessionToken || '',
                session: action.data?.session || null,
                callSheetWorkingShift: {
                    ...action.data?.callSheetWorkingShift,
                    upcoming: enrichedUpcoming,
                    slots: action.data?.slots || 0,
                    skills: action.data?.skills || '',
                    startTime: matchingReq.startTime || '',
                    endTime: matchingReq.endTime || '',
                } || null,
                requestParams: action.requestParams || null
            };
            // Use API-provided pendingCount, or derive from signup data as mock
            const pendingCount = action.data?.pendingCount ?? derivePendingCount(shiftSignups);

            return {
                ...state,
                employees,
                shiftSignups,
                fetchSuccess: true,
                fetchError: null,
                callSheetData,
                requirementFulfilled: false,
                pendingCount,
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_FAILED: {
            return {
                ...state,
                fetchSuccess: false,
                fetchError: action.data
            };
        }

        case overtimeConstants.POLL_CALLSHEET_SUCCESS: {
            // Silent poll update: refresh employee/signup data and pendingCount
            // but preserve requirementFulfilled, requestParams, and UI state
            const callSheetList = Array.isArray(action.data?.callSheetList)
                ? action.data.callSheetList
                : [];
            const { employees, shiftSignups } = normalizeEmployees(callSheetList);
            const pendingCount = action.data?.pendingCount ?? derivePendingCount(shiftSignups);

            return {
                ...state,
                employees,
                shiftSignups,
                pendingCount,
                callSheetData: {
                    ...state.callSheetData,
                    callSheetId: action.data?.votId || state.callSheetData?.callSheetId || '',
                    sessionToken: action.data?.session?.sessionToken || state.callSheetData?.sessionToken || '',
                    session: action.data?.session || state.callSheetData?.session || null,
                    callSheetWorkingShift: (() => {
                        const pollShiftsReqs = state.callSheetData?.requestParams?.shiftsReqs || [];
                        const pollCurrentShift = action.data?.callSheetWorkingShift?.current || '';
                        const pollUsedIndices = new Set();
                        const pollMatchIdx = pollShiftsReqs.findIndex(
                            (r) => (r.shiftType || '').toUpperCase() === pollCurrentShift.toUpperCase()
                        );
                        const pollMatchingReq = pollMatchIdx >= 0 ? pollShiftsReqs[pollMatchIdx] : {};
                        if (pollMatchIdx >= 0) pollUsedIndices.add(pollMatchIdx);
                        const pollUpcomingRaw = action.data?.callSheetWorkingShift?.upcoming || [];
                        const pollEnrichedUpcoming = pollUpcomingRaw.map((entry) => {
                            const shiftName = typeof entry === 'string'
                                ? entry
                                : (entry?.shiftName || entry?.shiftType || entry?.name || '');
                            const apiStartTime = entry !== null && typeof entry === 'object' ? (entry.startTime || '') : '';
                            const apiEndTime = entry !== null && typeof entry === 'object' ? (entry.endTime || '') : '';
                            const idx = pollShiftsReqs.findIndex(
                                (r, i) => !pollUsedIndices.has(i) && (r.shiftType || '').toUpperCase() === shiftName.toUpperCase()
                            );
                            if (idx >= 0) pollUsedIndices.add(idx);
                            const req = idx >= 0 ? pollShiftsReqs[idx] : {};
                            return { name: shiftName, startTime: apiStartTime || req.startTime || '', endTime: apiEndTime || req.endTime || '' };
                        });
                        return {
                            ...action.data?.callSheetWorkingShift,
                            upcoming: pollEnrichedUpcoming,
                            slots: action.data?.slots || 0,
                            skills: action.data?.skills || '',
                            startTime: pollMatchingReq.startTime || '',
                            endTime: pollMatchingReq.endTime || '',
                        };
                    })(),
                },
            };
        }

        case overtimeConstants.POLL_WB_STATUS:
            return { ...state, wbPolling: true };

        case overtimeConstants.POLL_WB_STATUS_SUCCESS: {
            const { results } = action;
            if (!results || Object.keys(results).length === 0) return { ...state, wbPolling: false };

            const updatedById = { ...state.shiftSignups.byId };
            let changed = false;

            Object.keys(updatedById).forEach((signupId) => {
                const signup = updatedById[signupId];
                const id = String(signup.reqId || signup.transactionId || '');
                if (!id || !results[id]) return;

                const result = results[id];
                if (result.status === 'APPLIED' || result.status === 'WB_SUCCESS') {
                    updatedById[signupId] = { ...signup, wbStatus: true, status: 'ASSIGNED' };
                    changed = true;
                } else if (result.status === 'WB_FAILURE') {
                    updatedById[signupId] = { ...signup, wbStatus: false, status: 'WB_FAILED' };
                    changed = true;
                }
            });

            if (!changed) return { ...state, wbPolling: false };

            const updatedSignups = { ...state.shiftSignups, byId: updatedById };
            return {
                ...state,
                shiftSignups: updatedSignups,
                pendingCount: derivePendingCount(updatedSignups),
                wbPolling: false,
            };
        }

        case overtimeConstants.TOGGLE_ITEM_COLLAPSED: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            const willExpand = employee.collapsed;
            
            // If expanding, collapse all others (accordion behavior)
            const updatedById = {};
            for (const id of state.employees.allIds) {
                const emp = state.employees.byId[id];
                if (id === action.employeeId) {
                    updatedById[id] = { ...emp, collapsed: !emp.collapsed };
                } else if (willExpand) {
                    // Collapse others when expanding this one
                    updatedById[id] = { ...emp, collapsed: true };
                } else {
                    updatedById[id] = emp;
                }
            }
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: updatedById
                }
            };
        }

        case overtimeConstants.SET_CONTACT: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: {
                        ...state.employees.byId,
                        [action.employeeId]: {
                            ...employee,
                            contact: {
                                ...employee.contact,
                                ...action.contact
                            }
                        }
                    }
                }
            };
        }

        case overtimeConstants.SELECT_DECISION: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            pendingDecision: action.decision
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION:
        case overtimeConstants.RESOLVE_OT_WB_FAILED: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            isConfirming: true
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION_SUCCESS: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            const decision = action.decision;
            if (!signup) return state;

            // WB_SUCCESS means Workbrain accepted the shift — treat as ASSIGNED
            const isWbSuccess = (decision || '').replace(/[_\s]/g, '').toUpperCase() === 'WBSUCCESS';
            
            const updatedShiftSignups = {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            status: isWbSuccess ? 'ASSIGNED' : decision,
                            wbStatus: isWbSuccess ? true : signup.wbStatus,
                            lastTimeStamp: action.lastTimeStamp || '',
                            reqId: action.reqId || signup.reqId || '',
                            savedAt: new Date().toISOString(),
                            isConfirming: false
                        }
                    }
                };
            return {
                ...state,
                shiftSignups: updatedShiftSignups,
                pendingCount: derivePendingCount(updatedShiftSignups),
            };
        }

        case overtimeConstants.SET_OVERTIME_SLOTS: {
            return {
                ...state,
                overtimeSlots: action.data
            };
        }

        case overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            status: action.resolveAction === 'NOACTION-NEEDED' ? 'REFUSED' : 'ACCEPT',
                            error: null,
                            resolvedAt: new Date().toISOString(),
                            isConfirming: false
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION_FAILED:
        case overtimeConstants.ADD_OT_SHIFT_FAILED:
        case overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            
            return {
                ...state,
                shiftSignups: signup ? {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: action.data,
                            confirmed: false,
                            isConfirming: false
                        }
                    }
                } : state.shiftSignups,
            };
        }

        case overtimeConstants.CLEAR_SIGNUP_ERROR: {
            const signupId = createSignupId(action.employeeId, action.shiftName);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: null
                        }
                    }
                }
            };
        }

        case overtimeConstants.GET_CALL_LOG_SUCCESS: {
            const apiEntries = Array.isArray(action.data?.callLog) ? action.data.callLog : [];
            const normalized = apiEntries.map((entry, idx) => {
                // Parse API timestamp "HH:MM MM/DD/YYYY" into ISO string
                const parseApiTimestamp = (ts) => {
                    if (!ts) return new Date().toISOString();
                    const match = ts.match(/^(\d{1,2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})$/);
                    if (match) {
                        const [, h, m, mo, d, y] = match;
                        return new Date(y, mo - 1, d, h, m).toISOString();
                    }
                    return ts;
                };
                const timestamp = parseApiTimestamp(entry.lastUpdated);
                const shiftLabel = (entry.shiftType || '').replace(/^SHIFT\s*/i, '').trim();

                // Action entries → system-style entries
                if (entry.action) {
                    const actionUpper = entry.action.toUpperCase();
                    let systemMessage = '';
                    if (actionUpper === 'ACCEPTED') systemMessage = 'WB Pending Sent to Workbrain';
                    else if (actionUpper === 'REJECTED') systemMessage = 'Refused shift';
                    else if (actionUpper === 'WB_FAILED') systemMessage = `Workbrain upload failed for ${shiftLabel}`;
                    else if (actionUpper === 'SAVE-ACCEPTED') systemMessage = `Saved accepted for ${shiftLabel}`;
                    else if (actionUpper === 'SAVE-REFUSED') systemMessage = `Saved refused for ${shiftLabel}`;
                    else systemMessage = `${entry.action} for ${shiftLabel}`;

                    return {
                        id: `api-${action.employeeId}-${idx}`,
                        isSystem: true,
                        action: entry.action,
                        systemMessage,
                        shiftLabel,
                        timestamp,
                        isFromApi: true,
                    };
                }

                // Call entries → call-style entries
                const callTypeVal = (entry.callType || '').charAt(0).toUpperCase() + (entry.callType || '').slice(1).toLowerCase();
                return {
                    id: `api-${action.employeeId}-${idx}`,
                    type: callTypeVal,
                    answered: entry.isAnswered ? 'answered' : (entry.isCallReturned ? 'received voicemail' : 'no answer'),
                    returnedCall: entry.isCallReturned ? 'yes' : 'no',
                    outcome: '',
                    notes: entry.callNotes || '',
                    timestamp,
                    isFromApi: true,
                };
            });
            return {
                ...state,
                callLogEntries: {
                    ...state.callLogEntries,
                    [action.employeeId]: normalized,
                },
            };
        }

        case overtimeConstants.OVERTIME_CLEANUP: {
            return {
                ...state,
                employees: { byId: {}, allIds: [] },
                shiftSignups: { byId: {}, byEmployeeId: {} },
                overtimeSlots: [],
                fetchSuccess: null,
                fetchError: null,
                callSheetData: null,
                otSkillsSuccess: null,
                otSkillsData: null,
                requirementFulfilled: false,
                pendingCount: null,
                callLogEntries: {},
            };
        }

        case overtimeConstants.GET_OT_SKILLS_DATA: {
            return {
                ...state,
                otSkillsData: action.payload
            };
        }

        case overtimeConstants.GET_OT_SKILLS_SUCCESS: {
            return {
                ...state,
                otSkillsSuccess: action.payload
            };
        }

        case overtimeConstants.SLOT_FULFILLED: {
            return {
                ...state,
                requirementFulfilled: true
            };
        }

        case overtimeConstants.RESET_SLOT_FULFILLED: {
            return {
                ...state,
                requirementFulfilled: false
            };
        }

        case overtimeConstants.GET_VOT_REPORT: {
            return {
                ...state,
                votReportLoading: true,
                votReportError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_SUCCESS: {
            return {
                ...state,
                votReportData: Array.isArray(action.data) ? action.data : [],
                votReportLoading: false,
                votReportError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_FAILED: {
            return {
                ...state,
                votReportLoading: false,
                votReportError: action.data,
            };
        }

        default:
            return state;
    }
};

export default OvertimeReducer;
